/**
 * PlanCard — карточка тарифа на странице /plans.
 *
 * Figma source:
 *   component-set `desk` (276:2198) — variants type=default / type=selected
 *   instances: 319:4656 (Ultra desk), 319:4657 (Premium), 319:4658 (Free)
 *
 * Layout (verbatim per docs/figma_component/desk-_base.json):
 *   FIXED 370 × HUG, padding 16/24/16/24, gap 12, corner-radius 16
 *   stroke 1px inside, border default = base/white @ 16%
 *   fill = linear-gradient 7° rgba(255,255,255,0) → rgba(153,153,153,0.16)
 *
 *   Row 1 — `radio` (102×24, gap 8): radio circle 24×24 + (optionally) text «Текущий»
 *           Raleway Bold 16, color text/secondary.
 *   Row 2 — `main` (322×43, HORIZONTAL gap 8):
 *           title (FILL, VERTICAL gap 4):
 *             — Frame 16 (HORIZONTAL gap 8): plan-name + discount-chip
 *             — «N кредитов / мес» Raleway Bold 14 text/secondary
 *           price (HUG, HORIZONTAL gap 8):
 *             — «$N» Raleway Bold 20 text/primary
 *             — «/ мес» Raleway Bold 14 text/secondary
 *   Row 3 — `more` (VERTICAL gap 4):
 *           dropdown (HORIZONTAL): «Преимущества плана» SemiBold 12 white +
 *             icon chevron-down 24×24 white. Features list hidden by default.
 *   Tag — `slider/tag` (94×22) — corner-attached top-right with bottom-left
 *           corner radius 12, orange gradient. Renders for `marketing.isBestChoice`.
 *
 * Discount chip (276:1934) — fill #269d46 @ 24% + background-blur, text bright
 * green. Здесь рендерим через токен text-text-success в bg/15% + text-100%.
 *
 * Props:
 *   id              ('FREE'|'PREMIUM'|'ULTRA')
 *   name            (string) — human name (Free / Premium / Ultra)
 *   price           (number) — USD price
 *   monthlyCredits  (number)
 *   signupCredits   (number) — one-time FREE welcome bonus
 *   features        (string[])
 *   method          ('card'|'crypto'|'sbp'|'stars')
 *   starsPrice      (number)
 *   sbpPrice        (number|null)
 *   marketing       ({ isBestChoice, discountPercent, oldPrice } | null)
 *   isCurrent       (boolean)
 *   isSelected      (boolean) — orange filled radio
 *   onSelect        (fn) — tap по карте (если !isCurrent)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../primitives';
import { appDateLocale } from '../../locales/content';

const PLAN_EMOJI = {
  FREE: '',
  PREMIUM: '\u{1F451}',
  ULTRA: '\u{1F48E}',
};

const NAME_CLASS = {
  FREE: 'text-text-primary',
  PREMIUM: 'plan-name-premium',
  ULTRA: 'plan-name-ultra',
};

function formatPrice(planKey, price, method, starsPrice, sbpPrice, freeText) {
  if (planKey === 'FREE') return freeText;
  // Figma 40000032:6639 — Stars price renders as "4 950 ⭐" (RU thousands +
  // U+2B50). The "/ мес" suffix stays — handled by the renderer below.
  if (method === 'stars') return `${starsPrice.toLocaleString(appDateLocale())} ⭐`;
  if (method === 'sbp' && sbpPrice != null) return `${sbpPrice} ₽`;
  return `$${price}`;
}

function formatOldPrice(planKey, marketing, method) {
  if (planKey === 'FREE' || !marketing?.oldPrice) return null;
  if (method === 'stars' || method === 'sbp') return null;
  return `$${marketing.oldPrice}`;
}

function formatCreditsLine(n, t) {
  if (n == null) return '';
  return `${n.toLocaleString(appDateLocale())} ${t('common.credits')} ${t('common.month')}`;
}

export default function PlanCard({
  id,
  name,
  price,
  monthlyCredits = null,
  signupCredits = null,
  features = [],
  method = 'card',
  starsPrice = 0,
  sbpPrice = null,
  marketing = null,
  isCurrent = false,
  isSelected = false,
  onSelect,
}) {
  const { t } = useTranslation();
  const planKey = (id || 'FREE').toUpperCase();
  const [expanded, setExpanded] = useState(false);

  const emoji = PLAN_EMOJI[planKey] || '';
  const nameClass = NAME_CLASS[planKey] ?? 'text-text-primary';
  const priceStr = formatPrice(planKey, price, method, starsPrice, sbpPrice, t('plans.freePrice'));
  const oldPriceStr = formatOldPrice(planKey, marketing, method);
  const creditsLine = planKey === 'FREE'
    ? t('plans.free.credits', { count: signupCredits ?? 10 })
    : formatCreditsLine(monthlyCredits, t);

  const handleCardActivate = () => {
    if (isCurrent) return;
    onSelect?.();
  };
  const handleKeyDown = (e) => {
    if (isCurrent) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect?.();
    }
  };
  const handleExpanderClick = (e) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  };

  // Radio circle — visual states per docs/figma_component/radio.json (24×24):
  //   isCurrent                → isSelected=yes, type=check (Figma 40002168:1534):
  //                              белая заливка @ 16% + серая обводка + check 16×16
  //   isSelected && !isCurrent → isSelected=yes, type=fill (Figma 40002168:420):
  //                              толстое оранжевое кольцо strokeWeight=8 INSIDE
  //                              (CSS border-8 даёт тот же 8px inset на 24×24)
  //   default                  → isSelected=no, type=fill:
  //                              серая обводка (surface/4 lvl ≈ glass-16)
  const radioBase = 'w-6 h-6 shrink-0 rounded-full flex items-center justify-center';
  const radioClass = isCurrent
    ? `${radioBase} bg-glass-16 border border-glass-16 text-text-primary`
    : isSelected
      ? `${radioBase} border-8 border-accent-orange-start`
      : `${radioBase} border-2 border-glass-16`;

  const surfaceClasses = [
    'relative rounded-home-16 overflow-hidden',
    'bg-glass-8',
    isSelected ? 'border border-accent-orange-start' : 'border border-glass-16',
    isCurrent ? 'cursor-default' : 'cursor-pointer',
  ].join(' ');

  return (
    <div
      role={isCurrent ? undefined : 'button'}
      tabIndex={isCurrent ? -1 : 0}
      aria-pressed={!isCurrent ? isSelected : undefined}
      onClick={handleCardActivate}
      onKeyDown={handleKeyDown}
      className={surfaceClasses}
      data-testid={`plan-card-${planKey.toLowerCase()}`}
      data-selected={isSelected ? 'true' : 'false'}
      data-current={isCurrent ? 'true' : 'false'}
    >
      {/* «Лучший выбор» — top-right corner-attached badge (94×22 in Figma) */}
      {marketing?.isBestChoice && (
        <div className="absolute top-0 right-0 z-10">
          <span
            data-testid={`plan-best-choice-${planKey.toLowerCase()}`}
            className="block bg-gradient-to-br from-accent-orange-start to-accent-orange-end text-text-inverse font-bold text-badge px-3 py-1 rounded-bl-home-16"
          >
            {t('common.bestChoice')}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-3 py-4 px-6">
        {/* Row 1 — radio + «Текущий» */}
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            data-testid={`plan-radio-${planKey.toLowerCase()}`}
            className={radioClass}
          >
            {isCurrent && <Icon name="check" size={16} />}
          </span>
          {isCurrent && (
            <span
              data-testid={`plan-current-marker-${planKey.toLowerCase()}`}
              className="font-sans text-[16px] font-bold leading-none text-text-secondary"
            >
              {t('common.current')}
            </span>
          )}
        </div>

        {/* Row 2 — title (name + chip + credits) | price (right) */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                data-testid={`plan-name-${planKey.toLowerCase()}`}
                className={[
                  'font-sans font-bold text-[20px] leading-none whitespace-nowrap',
                  nameClass,
                ].join(' ')}
              >
                {emoji && <span>{emoji} </span>}
                {name}
              </span>
              {marketing?.discountPercent > 0 && (
                <span
                  data-testid={`plan-discount-${planKey.toLowerCase()}`}
                  className="font-sans font-bold text-badge text-text-success bg-text-success/15 px-2 py-0.5 rounded-full whitespace-nowrap"
                >
                  -{marketing.discountPercent}%
                </span>
              )}
            </div>
            {creditsLine && (
              <span
                data-testid={`plan-credits-${planKey.toLowerCase()}`}
                className="font-sans font-bold text-[14px] leading-tight text-text-secondary"
              >
                {creditsLine}
              </span>
            )}
          </div>

          {planKey !== 'FREE' && (
            <div className="flex items-baseline gap-1 shrink-0">
              {oldPriceStr && (
                <span className="font-sans text-plan text-text-tertiary line-through">
                  {oldPriceStr}
                </span>
              )}
              <span className="font-sans font-bold text-[20px] leading-none text-text-primary">
                {priceStr}
              </span>
              <span className="font-sans font-bold text-[14px] leading-none text-text-secondary">
                {t('common.month')}
              </span>
            </div>
          )}
        </div>

        {/* Row 3 — collapsible features («Преимущества плана ▼») */}
        {features.length > 0 && (
          <div>
            <button
              type="button"
              onClick={handleExpanderClick}
              aria-expanded={expanded}
              data-testid={`plan-features-toggle-${planKey.toLowerCase()}`}
              className="flex items-center gap-1 font-sans font-semibold text-[12px] leading-4 text-text-primary"
            >
              {t('common.advantages')}
              <span
                aria-hidden="true"
                className={[
                  'inline-flex transition-transform',
                  expanded ? 'rotate-180' : '',
                ].join(' ')}
              >
                <Icon name="chevron-down" size={24} />
              </span>
            </button>

            {expanded && (
              <ul
                data-testid={`plan-features-list-${planKey.toLowerCase()}`}
                className="flex flex-col gap-2 mt-3"
              >
                {features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span
                      className="mt-0.5 shrink-0 text-icon-accent"
                      aria-hidden="true"
                    >
                      <Icon name="check" size={12} />
                    </span>
                    <span className="font-sans text-plan text-text-secondary leading-relaxed">
                      {feat}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
